import {
  Box,
  Button,
  Chip,
  Dialog,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Popover,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Facebook from "./FacebookCard";
import EmojiPicker from "emoji-picker-react";
import { useContext, useState } from "react";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import strings from "global/constants/StringConstants";
import Instagram from "./InstagramCard";
import { SocialCampaignContext } from "../Context";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropZoneIcon from "assets/icons1/dropzone_icon.svg";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { uploadImage } from "screens/Integration/Component/FacebookService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { cloneDeep } from "lodash";

interface CustomProps {
  handleDialogClose: Function;
  isDialogOpen: boolean;
  formFields: any;
  setFormFields: Function;
  carousalIndex: number;
  setCarousalIndex: Function;
  postImage: string[];
  setPostImage: Function;
}

const DesignPost = (props: CustomProps) => {
  const { postImage, setPostImage, carousalIndex, setCarousalIndex } = props;
  const [openEmojiPicker, setOpenEmojiPicker] =
    useState<HTMLButtonElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const acceptedFilesExtension: string[] = [".jpeg", ".jpg", ".png"];
  const { submitCampaign, setSubmitCampaign } = useContext(
    SocialCampaignContext
  );
  const selectedSocialApp = submitCampaign.socialAppName;
  const totalAllowedUploadImages =
    selectedSocialApp === strings.FACEBOOK ? 4 : 10;

  const checkFileExtension = (imageName: string) => {
    const fileExtension = imageName
      .substring(imageName.lastIndexOf("."))
      .toLowerCase();
    const isPresent = acceptedFilesExtension.includes(fileExtension);
    return !isPresent;
  };

  const isImageDimensionNotValid = async (image: any) => {
    let isInvalid: boolean = true;
    let aspectRatio = 0;
    if (image && image.type.startsWith("image/")) {
      const objectURL = URL.createObjectURL(image);
      const img = new Image();
      img.src = objectURL;
      await img.decode();
      if (img.width > img.height) {
        aspectRatio = img.width / img.height;
      } else {
        aspectRatio = img.height / img.width;
      }
      const roundOff: number = parseFloat(aspectRatio.toFixed(2));
      isInvalid = roundOff < 0.8 || roundOff > 1.91;
    }
    return isInvalid;
  };

  const isDuplicateImagePresent = (imageName: string) => {
    const postImageNames: string[] = props.formFields.image.value.map(
      (image: any) => image.name
    );
    const isPresent = postImageNames.some((name: string) => name === imageName);
    return isPresent;
  };

  const setErrorMessage = (
    mainErrorArray: string[],
    individualErrorArray: string[],
    errorMessage: string
  ) => {
    if (isTruthy(individualErrorArray)) {
      mainErrorArray.push(errorMessage);
    }
  };

  const handleDeleteDropZoneFile = (index: number) => {
    props.setFormFields({
      ...props.formFields,
      image: {
        ...props.formFields["image"],
        value: props.formFields.image.value.filter(
          (item: any, itemIndex: number) => itemIndex !== index
        ),
      },
    });
    setPostImage(
      postImage.filter((item: any, itemIndex: number) => itemIndex !== index)
    );
    setCarousalIndex(0);
  };

  const handleOnDropZoneFile = async (event: any) => {
    try {
      let postDataImageArray: any[] = [...props.formFields.image.value];
      let postImageArray: string[] = [...postImage];
      let duplicateImagesArray: string[] = [];
      let reject4mbFiles: string[] = [];
      let rejectedDimensionFiles: string[] = [];
      let invalidExtensionFiles: string[] = [];
      let errorsArray: string[] = [];

      if (!(event.length <= totalAllowedUploadImages - postImage.length)) {
        return openErrorNotification(
          `Only ${totalAllowedUploadImages} images can be uploaded`
        );
      }
      setIsLoading(true);
      for (const element of event) {
        const imageName = element.name;
        const imageSize = element.size;
        const isFileExtensionInCorrect = checkFileExtension(imageName);
        const isImagePresent = isDuplicateImagePresent(imageName);
        const invalidDimension = await isImageDimensionNotValid(element);
        switch (true) {
          case isFileExtensionInCorrect:
            invalidExtensionFiles.push(imageName);
            break;
          case isImagePresent:
            duplicateImagesArray.push(imageName);
            break;
          case imageSize > 4194304:
            reject4mbFiles.push(imageName);
            break;
          case selectedSocialApp === strings.INSTAGRAM && invalidDimension:
            rejectedDimensionFiles.push(imageName);
            break;
          default:
            const data = new FormData();
            data.append("file", element);
            const response = await uploadImage(data);
            postImageArray.push(response.message);
            postDataImageArray.push(element);
            break;
        }
      }
      setIsLoading(false);
      setPostImage(postImageArray);
      props.setFormFields({
        ...props.formFields,
        image: {
          value: postDataImageArray,
          error: "",
        },
      });
      setErrorMessage(
        errorsArray,
        invalidExtensionFiles,
        `${invalidExtensionFiles.join(
          ", "
        )} doesn't have valid file extension. `
      );
      setErrorMessage(
        errorsArray,
        duplicateImagesArray,
        `${duplicateImagesArray.join(", ")} ${
          duplicateImagesArray.length > 1 ? "are" : "is"
        } already uploaded. `
      );
      setErrorMessage(
        errorsArray,
        reject4mbFiles,
        `${reject4mbFiles.join(", ")} ${
          reject4mbFiles.length > 1 ? "are" : "is"
        } over 4 MB. `
      );
      setErrorMessage(
        errorsArray,
        rejectedDimensionFiles,
        `${rejectedDimensionFiles.join(
          ", "
        )} does not have the aspect ratio between 0.8 to 1.96. `
      );
      if (isTruthy(errorsArray)) {
        openErrorNotification(errorsArray);
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDoneEditing = () => {
    let error = false;
    const clonedFormFields = cloneDeep(props.formFields);
    if (
      submitCampaign.socialAppName === strings.FACEBOOK &&
      !isTruthy(clonedFormFields.message.value) &&
      clonedFormFields.image.value.length === 0
    ) {
      error = true;
      clonedFormFields.message.error =
        "Please either enter content or upload image or both";
      clonedFormFields.image.error =
        "Please either enter content or upload image or both";
    }
    if (
      submitCampaign.socialAppName === strings.INSTAGRAM &&
      clonedFormFields.image.value.length === 0
    ) {
      error = true;
      clonedFormFields.message.error =
        "Please write content for instagram campaign";
      clonedFormFields.image.error =
        "Please upload images for Instagram campaign";
    }
    if (error) {
      props.setFormFields(clonedFormFields);
    } else {
      props.handleDialogClose();
      setSubmitCampaign({
        ...submitCampaign,
        socialCampaign: {
          ...submitCampaign.socialCampaign,
          message: props.formFields.message.value,
          link: postImage,
        },
      });
    }
  };

  const getPageHeader = () => {
    return (
      <Stack
        p={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack>
          <Button variant="outlined" onClick={() => props.handleDialogClose()}>
            Go Back
          </Button>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={() => handleDoneEditing()}>
            Done editing
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getSelectedAppCard = () => {
    switch (submitCampaign.socialAppName) {
      case strings.FACEBOOK: {
        return (
          <Facebook
            formFields={props.formFields}
            imageArray={postImage}
            carousalIndex={carousalIndex}
            setCarousalIndex={setCarousalIndex}
          />
        );
      }
      case strings.INSTAGRAM: {
        return (
          <Instagram
            formFields={props.formFields}
            imageArray={postImage}
            carousalIndex={carousalIndex}
            setCarousalIndex={setCarousalIndex}
          />
        );
      }
    }
  };

  const getEdior = () => {
    return (
      <Grid container>
        <Grid item xs={12} md={6} p={2}>
          <Box mb={1}>
            <InputLabel required>
              Content
            </InputLabel>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={5}
            placeholder="Enter your content here..."
            variant="outlined"
            value={props.formFields.message.value}
            onChange={(event: any) => {
              props.setFormFields({
                ...props.formFields,
                message: {
                  value: event.target.value,
                  error: "",
                },
              });
            }}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                padding: "13px 13px",
              },
            }}
            error={isTruthy(props.formFields.message.error)}
            helperText={props.formFields.message.error}
            inputProps={{ minLength: 1, maxLength: 60000 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ fontSize: "0.75rem" }}>
                    {props.formFields.message.value.length} / 60000
                  </span>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" justifyContent="flex-end">
            <Tooltip title="Add emojis">
              <IconButton
                onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                  setOpenEmojiPicker(event.currentTarget)
                }
              >
                <SvgIcon>
                  <EmojiEmotionsIcon htmlColor="#7551D0" fontSize="small" />
                </SvgIcon>
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(openEmojiPicker)}
              anchorEl={openEmojiPicker}
              onClose={() => setOpenEmojiPicker(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <EmojiPicker
                onEmojiClick={(emoji) => {
                  props.setFormFields({
                    ...props.formFields,
                    message: {
                      value: `${props.formFields.message.value}${emoji.emoji}`,
                      error: "",
                    },
                  });
                }}
              />
            </Popover>
          </Stack>

          <Box pt={1}>
            <Box mb={1}>
              <InputLabel required>Media</InputLabel>
            </Box>
            <CustomDropzone
              sx={{ height: "8rem" }}
              onAdd={handleOnDropZoneFile}
              maxFileSize={85 * 1024 * 1024}
              acceptedFiles={acceptedFilesExtension}
              showAlerts={false}
              multipleFileUpload={false}
              dropzoneText={"Choose a file or drag & drop it here"}
              icon={DropZoneIcon}
            />
            <FormHelperText error>
              {props.formFields.image.error}
            </FormHelperText>
            <Box pt={2}>
              {props.formFields.image.value.map((item: any, index: number) => {
                return (
                  <>
                    <Chip
                      label={item.name}
                      variant="filled"
                      onClick={() => {
                        setCarousalIndex(index);
                      }}
                      sx={{
                        mr: 1,
                      }}
                      onDelete={() => handleDeleteDropZoneFile(index)}
                    />
                  </>
                );
              })}
            </Box>

            <Typography variant="subtitle2" color={"#FF9436"} pt={1}>
              {`Only ${totalAllowedUploadImages} files of type .png/.jpg/.jpeg and size less than 4MB each
                are allowed.`}
              {selectedSocialApp === strings.INSTAGRAM &&
                " The aspect ratio of the images should be between 0.8 and 1.96."}
            </Typography>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "#DCE2EB",
          }}
        >
          {getSelectedAppCard()}
        </Grid>
      </Grid>
    );
  };

  const getPageBody = () => {
    return (
      <Stack direction="row" height="100%" p={2}>
        {getEdior()}
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Stack justifyContent="space-between" height="100vh !important">
        {getPageHeader()}
        {getPageBody()}
      </Stack>
    );
  };
  return (
    <>
      <Dialog fullWidth fullScreen open={props.isDialogOpen}>
        {getBody()}
      </Dialog>
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default DesignPost;
